/**
 * „Daten ergänzen“ im Produkteditor: Treffer aus den freien Datenbanken
 * (über die EAN der Packungen) Feld für Feld neben den eigenen Angaben.
 * Vorausgewählt sind nur Felder, die bisher leer sind; abweichende eigene
 * Angaben bleiben, solange sie nicht ausdrücklich angehakt werden.
 */
import { NUTRIENT_FIELDS, allergenNames, categoryLabel, formatDecimal, labelNames } from '@cookbook/domain';
import type { Nutrients } from '@cookbook/domain';
import { useEffect, useState } from 'react';
import { ApiError, api, isOffline } from '../../api/client.ts';
import type { FoundProduct, Product, ProductVariant } from '../../api/types.ts';
import { texts } from '../../texts/de.ts';
import { SourceNote } from './SourceNote.tsx';

/** Vorschlag zu einer Packung. */
interface Suggestion {
  /** null: Vorschlag für das Produkt allgemein (Nährwerte, Bild). */
  variantId: string | null;
  found: FoundProduct;
}

/** Eine Zeile des Vergleichs. */
interface Row {
  readonly field: string;
  readonly label: string;
  readonly current: string | null;
  readonly found: string | null;
  readonly currentImage?: string | null;
  readonly foundImage?: string | null;
}

/** Kurzform der Nährwerte. */
function nutritionText(values: Nutrients | null): string | null {
  if (values === null) {
    return null;
  }
  return NUTRIENT_FIELDS.filter((field) => field.key !== 'sugar')
    .map(
      (field) =>
        `${formatDecimal(values[field.key], field.key === 'kcal' ? 0 : 1)} ${field.unit} ${texts.nutrition.fields[field.key]}`,
    )
    .join(' · ');
}

/** Allergene und Siegel als Text, null ohne bekannte Angaben. */
function detailsText(allergens: readonly string[], labels: readonly string[]): string | null {
  const parts = [...allergenNames(allergens), ...labelNames(labels)];
  return parts.length === 0 ? null : parts.join(', ');
}

/** Inhalt einer Packung als Text. */
function packageText(amount: number | null, unit: string): string | null {
  return amount === null ? null : `${formatDecimal(amount)} ${unit}`;
}

/** Vergleichszeilen für einen Treffer. */
function rowsFor(product: Product, variant: ProductVariant | undefined, found: FoundProduct): Row[] {
  return [
    {
      field: 'nutrition',
      label: texts.supplement.nutrition,
      current: nutritionText(product.nutrition),
      found: nutritionText(found.nutrition),
    },
    {
      field: 'category',
      label: texts.products.category,
      current: categoryLabel(product.category),
      found: categoryLabel(found.category),
    },
    {
      field: 'productImage',
      label: texts.supplement.productImage,
      current: product.thumbUrl === null ? null : '',
      found: found.imageUrl === null ? null : '',
      currentImage: product.thumbUrl,
      foundImage: found.imageUrl,
    },
    { field: 'brand', label: texts.products.brand, current: variant?.brand ?? null, found: found.brand },
    { field: 'label', label: texts.products.label, current: variant?.label ?? null, found: found.name },
    {
      field: 'package',
      label: texts.products.packageAmount,
      current: packageText(variant?.packageAmount ?? null, variant?.packageUnit ?? ''),
      found: packageText(found.packageAmount, found.packageUnit),
    },
    {
      field: 'details',
      label: texts.supplement.details,
      current: detailsText(variant?.allergens ?? [], variant?.labels ?? []),
      found: detailsText(found.allergens, found.labels),
    },
    {
      field: 'variantImage',
      label: texts.supplement.variantImage,
      current: variant?.thumbUrl ? '' : null,
      found: found.imageUrl === null ? null : '',
      currentImage: variant?.thumbUrl ?? null,
      foundImage: found.imageUrl,
    },
  ];
}

/** Lässt sich die Zeile übernehmen, und ist sie vorausgewählt? */
function rowState(row: Row): { usable: boolean; preselected: boolean } {
  const usable = row.found !== null && (row.found !== row.current || row.foundImage !== undefined);
  return { usable, preselected: usable && row.current === null };
}

/** Ein Treffer mit Vergleich und Übernahme. */
function SuggestionCard({
  product,
  suggestion,
  onApplied,
}: {
  readonly product: Product;
  readonly suggestion: Suggestion;
  readonly onApplied: (product: Product, warnings: readonly string[]) => void;
}) {
  const variant = product.variants.find((entry) => entry.id === suggestion.variantId);
  // Allgemeine Vorschläge (USDA, Commons) betreffen nur Nährwerte und Bild des Produkts.
  const rows = rowsFor(product, variant, suggestion.found).filter(
    (row) =>
      suggestion.variantId !== null ||
      ((row.field === 'nutrition' || row.field === 'productImage') && row.found !== null),
  );
  const [chosen, setChosen] = useState<ReadonlySet<string>>(
    () => new Set(rows.filter((row) => rowState(row).preselected).map((row) => row.field)),
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function apply() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.post<{ product: Product; warnings: string[] }>(
        `/api/products/${product.id}/apply-source`,
        {
          source: suggestion.found.source,
          sourceId: suggestion.found.sourceId,
          variantId: suggestion.variantId,
          fields: [...chosen],
        },
      );
      onApplied(result.product, result.warnings);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded-xl border border-border-subtle p-3">
      <p className="text-sm">
        <span className="font-semibold">{suggestion.found.name}</span>{' '}
        <span className="text-content-muted">
          ·{' '}
          {suggestion.variantId === null
            ? texts.supplement.general.replace('{source}', suggestion.found.sourceLabel)
            : `${texts.supplement.forPackage} ${variant?.ean ?? ''}`}
        </span>
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-content-muted">
            <th className="w-8 py-1" />
            <th className="py-1 font-normal">{texts.supplement.field}</th>
            <th className="py-1 pr-2 font-normal">{texts.supplement.current}</th>
            <th className="py-1 font-normal">{texts.supplement.found}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { usable } = rowState(row);
            const id = `${suggestion.variantId}-${row.field}`;
            return (
              <tr
                key={row.field}
                className={`border-t border-border-subtle align-top ${usable ? '' : 'opacity-50'}`}
              >
                <td className="py-1.5">
                  <input
                    id={id}
                    type="checkbox"
                    disabled={!usable}
                    checked={chosen.has(row.field)}
                    onChange={(event) => {
                      const next = new Set(chosen);
                      if (event.target.checked) {
                        next.add(row.field);
                      } else {
                        next.delete(row.field);
                      }
                      setChosen(next);
                    }}
                    className="accent-accent"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <label htmlFor={id}>{row.label}</label>
                </td>
                <td className="py-1.5 pr-2 text-content-muted">
                  {row.currentImage === undefined ? (
                    (row.current ?? '–')
                  ) : row.currentImage === null ? (
                    '–'
                  ) : (
                    <img src={row.currentImage} alt="" className="h-10 w-10 rounded-lg object-cover" />
                  )}
                </td>
                <td className="py-1.5">
                  {row.foundImage === undefined ? (
                    (row.found ?? '–')
                  ) : row.foundImage === null ? (
                    '–'
                  ) : (
                    <img
                      src={row.foundImage}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="h-10 w-10 rounded-lg bg-white object-contain"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <SourceNote found={suggestion.found} />
      {message === null ? null : <p className="text-sm text-accent-strong">{message}</p>}
      <button
        type="button"
        onClick={() => void apply()}
        disabled={busy || chosen.size === 0}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
      >
        {busy
          ? texts.scan.adopting
          : chosen.size === 1
            ? texts.supplement.applyOne
            : texts.supplement.apply.replace('{count}', String(chosen.size))}
      </button>
    </div>
  );
}

/** Bereich „Daten ergänzen“. */
export function SupplementPanel({
  product,
  onApplied,
}: {
  readonly product: Product;
  readonly onApplied: (product: Product) => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const hasEan = product.variants.some((variant) => variant.ean !== null);
  const eans = product.variants.map((variant) => variant.ean ?? '').join(',');

  useEffect(() => {
    let alive = true;
    setSuggestions(null);
    setMessage(null);
    api
      .get<{ suggestions: Suggestion[]; enabled: boolean }>(`/api/products/${product.id}/suggestions`)
      .then((result) => {
        if (alive) {
          setSuggestions(result.suggestions);
          if (!result.enabled) {
            setMessage(texts.scan.searchDisabled);
          }
        }
      })
      .catch((caught: unknown) => {
        if (alive) {
          setMessage(
            isOffline(caught)
              ? texts.scan.offline
              : caught instanceof ApiError
                ? caught.message
                : texts.common.error,
          );
        }
      });
    return () => {
      alive = false;
    };
    // Neu laden, wenn sich Name oder Packungen (EAN) ändern.
  }, [product.id, product.name, eans]);

  return (
    <section className="space-y-3 rounded-card border border-border-subtle bg-surface-raised p-4">
      <h2 className="font-semibold">{texts.supplement.title}</h2>
      {message !== null ? (
        <p className="text-sm text-content-muted">{message}</p>
      ) : suggestions === null ? (
        <p className="text-sm text-content-muted">{texts.common.loading}</p>
      ) : suggestions.length === 0 ? (
        <p className="text-sm text-content-muted">{texts.supplement.none}</p>
      ) : (
        <>
          <p className="text-sm text-content-muted">{texts.supplement.hint}</p>
          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={`${suggestion.found.source}-${suggestion.found.sourceId}-${suggestion.variantId ?? ''}-${product.provenance.length}-${product.variants.map((v) => v.provenance.length).join()}`}
              product={product}
              suggestion={suggestion}
              onApplied={(updated, warnings) => {
                setNotice(warnings.length > 0 ? warnings.join(' ') : texts.supplement.applied);
                onApplied(updated);
              }}
            />
          ))}
        </>
      )}
      {notice === null ? null : <p className="text-sm text-accent-strong">{notice}</p>}
      {hasEan ? null : <p className="text-xs text-content-muted">{texts.supplement.needEan}</p>}
    </section>
  );
}
